const normalizeText = (value) => String(value || '').trim().toLowerCase();

const getCandidateKey = (entry = {}, entryConfig = null) => normalizeText(
  entryConfig?.behaviorType
  || entry.behaviorType
  || entryConfig?.editTarget
  || entry.editTarget
  || entryConfig?.kind
  || entryConfig?.type
  || entryConfig?.category
  || entry.category
  || entry.timelineType
  || entry.type
  || entry.logCategory
  || entry.categoryLabel
  || ''
);

const getSleepDetails = (entry = {}) => entry.sleepDetails || entry.data?.sleepDetails || {};

const hasFullStructuredSleepMetadata = (entry = {}) => {
  const sleepDetails = getSleepDetails(entry);

  return Boolean(
    sleepDetails.startAt
    || sleepDetails.startTime
    || sleepDetails.bedtime
    || sleepDetails.endAt
    || sleepDetails.endTime
    || sleepDetails.wakeTime
    || sleepDetails.durationMin != null
    || sleepDetails.durationHours != null
    || entry.startAt
    || entry.startTime
    || entry.bedtime
    || entry.endAt
    || entry.endTime
    || entry.wakeTime
    || entry.durationMin != null
    || entry.durationHours != null
  );
};

const isStateSnapshotEntry = (entry = {}) => (
  normalizeText(entry.collection) === 'dailycare'
  && normalizeText(entry.actionType) === 'mood'
);

const isSmartCaptureObservation = (entry = {}) => Boolean(
  entry.parsedObservation
  || entry?.source?.parsedObservation
  || entry?.originalData?.parsedObservation
);

const isSourceOrganizedTimelineEntry = (entry = {}) => Boolean(
  entry.sourceNoteId
  || entry.metadata?.sourceNoteId
  || entry.metadata?.captureFirst?.enabled
  || entry.source?.sourceNoteId
  || entry.originalData?.sourceNoteId
  || entry.originalData?.metadata?.sourceNoteId
  || entry.originalData?.metadata?.captureFirst?.enabled
);

const isSleepEntry = (entry = {}, candidateKey = '') => (
  candidateKey === 'sleep'
  || normalizeText(entry.category) === 'sleep'
  || normalizeText(entry.type) === 'sleep'
  || normalizeText(entry.timelineType) === 'sleep'
  || normalizeText(entry.logCategory) === 'sleep'
);

const isStructuredLogEntry = (candidateKey = '') => (
  candidateKey === 'food'
  || candidateKey === 'medication'
  || candidateKey === 'medication_change'
  || candidateKey === 'meds'
  || candidateKey === 'medicine'
  || candidateKey === 'pill'
  || candidateKey === 'bathroom'
  || candidateKey === 'toileting'
  || candidateKey === 'toilet'
  || candidateKey === 'diaper'
  || candidateKey === 'activity'
  || candidateKey === 'behavior'
  || candidateKey === 'behaviour'
  || candidateKey === 'incident'
  || candidateKey === 'meltdown'
  || candidateKey === 'trigger'
);

export const getTimelineEntryBehaviorType = (entry = {}, entryConfig = null) => {
  const candidateKey = getCandidateKey(entry, entryConfig);

  if (candidateKey === 'state_snapshot') {
    return 'state_snapshot';
  }

  if (candidateKey === 'tracker') {
    return 'tracker';
  }

  if (candidateKey === 'structured_log') {
    return 'structured_log';
  }

  if (candidateKey === 'narrative_note') {
    return 'narrative_note';
  }

  if (isStateSnapshotEntry(entry)) {
    return 'state_snapshot';
  }

  if (normalizeText(entry.category) === 'mood' && isSmartCaptureObservation(entry)) {
    return 'narrative_note';
  }

  if (isSourceOrganizedTimelineEntry(entry)) {
    return 'narrative_note';
  }

  if (isSleepEntry(entry, candidateKey)) {
    return hasFullStructuredSleepMetadata(entry) ? 'tracker' : 'narrative_note';
  }

  if (candidateKey === 'mood') {
    return 'narrative_note';
  }

  if (isStructuredLogEntry(candidateKey)) {
    return 'structured_log';
  }

  return 'narrative_note';
};

export const getTimelineEntryEditTarget = (entry = {}, entryConfig = null) => {
  const behaviorType = getTimelineEntryBehaviorType(entry, entryConfig);
  const candidateKey = getCandidateKey(entry, entryConfig);

  if (behaviorType === 'state_snapshot') {
    return 'mood';
  }

  if (behaviorType === 'tracker') {
    return 'sleep';
  }

  if (behaviorType === 'structured_log') {
    if (
      candidateKey === 'medication_change'
      || candidateKey === 'medication'
      || candidateKey === 'meds'
      || candidateKey === 'medicine'
      || candidateKey === 'pill'
    ) {
      return 'medication';
    }

    if (candidateKey === 'food') {
      return 'food';
    }

    if (
      candidateKey === 'bathroom'
      || candidateKey === 'toileting'
      || candidateKey === 'toilet'
      || candidateKey === 'diaper'
    ) {
      return 'bathroom';
    }

    if (candidateKey === 'activity') {
      return 'activity';
    }

    if (
      candidateKey === 'behavior'
      || candidateKey === 'behaviour'
      || candidateKey === 'incident'
      || candidateKey === 'meltdown'
      || candidateKey === 'trigger'
    ) {
      return 'behavior';
    }
  }

  return 'note';
};

export const hasStructuredSleepMetadata = hasFullStructuredSleepMetadata;
export const isSmartCaptureObservationEntry = isSmartCaptureObservation;
